import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CirclePause, Info } from 'lucide-react';
import { EDM_UI, canResumeForReason, pausedReasonCopy } from '../../../../utils/edm';

const FREEZING_STALE_MS = 10 * 60 * 1000;

/**
 * Shown for a paused campaign, a freeze running longer than 10 minutes, or a
 * refusal from the last schedule attempt. Heading colour matches the panel.
 */
const StatusBanner: React.FC<{
  status: string;
  pausedReason: string | null;
  updatedAt: string | null;
  role?: string;
  actionError: string | null;
  busy: boolean;
  onResume: () => void;
}> = ({ status, pausedReason, updatedAt, role, actionError, busy, onResume }) => {
  const freezingStale =
    status === 'freezing' && updatedAt !== null && Date.now() - new Date(updatedAt).getTime() > FREEZING_STALE_MS;

  return (
    <>
      {status === 'paused' && (
        <PausedBanner reason={pausedReason} role={role} busy={busy} onResume={onResume} />
      )}
      {freezingStale && (
        <div className={`${EDM_UI.infoBanner} flex gap-2`}>
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>
            Freezing the list is taking longer than usual. Refresh in a minute. If it stays like this, contact VALUTTEN
            support.
          </span>
        </div>
      )}
      {actionError && (
        <div className={`${EDM_UI.errorBanner} flex gap-2`} role="alert">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{actionError}</span>
        </div>
      )}
    </>
  );
};

const PausedBanner: React.FC<{
  reason: string | null;
  role?: string;
  busy: boolean;
  onResume: () => void;
}> = ({ reason, role, busy, onResume }) => {
  const copy = pausedReasonCopy(reason);
  const showResume = canResumeForReason(reason, role);
  const showLink = copy.link && (!copy.link.superAdminOnly || role === 'super_admin');
  return (
    <div className="space-y-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-6">
      <h2 className="flex items-center gap-2 text-base font-semibold text-amber-300">
        <CirclePause className="h-4 w-4" /> Sending is paused
      </h2>
      <p className="text-sm text-amber-200">{copy.body}</p>
      <div className="flex flex-wrap items-center gap-3">
        {showLink && copy.link && (
          <Link to={copy.link.to} className="text-sm text-amber-200 underline hover:text-amber-100">
            {copy.link.label}
          </Link>
        )}
        {showResume ? (
          <button type="button" className={EDM_UI.primaryButton} disabled={busy} onClick={onResume}>
            Resume
          </button>
        ) : (
          copy.resume === 'super_admin_only' && (
            <span className="text-sm text-amber-200">Only a Super Admin can resume this campaign.</span>
          )
        )}
      </div>
    </div>
  );
};

export default StatusBanner;
